import { ComponentInterface, Entity } from '@goodboydigital/odie';

import { Maths } from '../../utils/Maths';
import { VehicleEntity } from '../entities/VehicleEntity';

export class MotorComponent implements ComponentInterface
{
    public vehicle!: VehicleEntity;
    public acceleration = 0;
    public lean = 0;
    public leanTorque = 0;
    public torque = 0;
    public torqueMax = 0.7;
    public damping = 0.9;
    public isOn = false;
    public boostTime = 0;
    public boostPower = 0;
    public idleRunning = false;
    public handBrake = false;

    public maxSpeedMultiplier = 1;
    public flipSpeedMultiplier = 1;

    public addedToEntity(entity: Entity): void
    {
        if (!(entity instanceof VehicleEntity)) throw new Error('Player component only works with a VehicleEntity');
        this.vehicle = entity as VehicleEntity;
        this.vehicle.physics.physicsFixedUpdateRunner.add(this as any);
    }

    public reset(): void
    {
        this.acceleration = 0;
        this.lean = 0;
        this.damping = 0.9;
        this.boostTime = 0;
        this.boostPower = 0;
        this.handBrakeOff();
    }

    public turnOn(): void
    {
        this.idleRunning = false;
        this.isOn = true;
        this.reset();
    }

    public turnOff(): void
    {
        this.idleRunning = false;
        this.isOn = false;
        this.reset();
    }

    public idleRun(): void
    {
        this.reset();
        this.idleRunning = true;
    }

    public physicsFixedUpdate(): void
    {
        if (!this.vehicle) return;
        if (this.idleRunning)
        {
            this.updateIdleRunning();
        }
        else
        {
            if (this.isOn)
            {
                this.updateAcceleration();
                this.updateLean();
            }
            this.updateWheelDamping();
        }

        if (this.boostTime)
        {
            this.boostTime -= (1 / 60);
            if (this.boostTime < 0) this.boostTime = 0;
        }

        if (this.handBrake) this.updateHandBrake();
    }

    private updateAcceleration(): void
    {
        if (!this.vehicle || this.vehicle.crashed) return;
        const wheelLeft = this.vehicle.wheelLeft.physics.bodies[0];
        const wheelRight = this.vehicle.wheelRight.physics.bodies[0];

        const statSpeed = this.vehicle.calculateSpeed();
        const statHandling = this.vehicle.calculateHandling();

        let speedCeil = 45 + (10 * statSpeed); // 45~55 where 45 = slower and 55 = faster

        // Increase speed ceiling by bonus max speed multiplier
        speedCeil *= this.maxSpeedMultiplier;

        // Allow go over the top speed if boost is on
        if (this.boostTime) speedCeil *= 1.3;

        const brakePower = Math.min((1 - statHandling), 0.95); // 0~1 where 0 = instant brake; 1 = no brake
        let torquePower = (statHandling + statSpeed) * 0.5; // 0~1 where 0 = slow acc; 1 = fast acc

        // Increase torque power if boosting
        if (this.boostTime && this.boostPower) torquePower *= (5 * this.boostPower);

        // Hard brake if acceleration has changed direction
        if (this.acceleration !== 0 && Maths.sign(this.acceleration) !== Maths.sign(-wheelLeft.angularVelocity))
        {
            wheelLeft.angularVelocity *= brakePower;
            wheelRight.angularVelocity *= brakePower;
        }

        // Build up torque to serve as wheels angular velocity
        this.torque = (13000 + (2000 * torquePower)) * this.acceleration;
        const angularForceLeft = -this.torque;
        const angularForceRight = -this.torque;

        // Only set angular velocity if wheels are spinning slower than the angular velocity ceiling
        // this way we can control max speed without locking natural rotation built from momentum
        if (Math.abs(wheelLeft.angularVelocity) < speedCeil) wheelLeft.angularForce = angularForceLeft;
        if (Math.abs(wheelRight.angularVelocity) < speedCeil) wheelRight.angularForce = angularForceRight;
    }

    private updateLean(): void
    {
        if (!this.vehicle) return;

        const statFlipSpeed = this.vehicle.calculateFlipSpeed();
        const normalisedLean = this.getNormalisedLean(this.lean) * this.flipSpeedMultiplier;
        const changedDir = Math.sign(this.leanTorque) !== Math.sign(normalisedLean);

        this.leanTorque = (60000 + (20000 * statFlipSpeed)) * normalisedLean;
        const body = this.vehicle.chassis.physics.bodies[0];

        if (Math.abs(body.angularVelocity) < 3 || changedDir) body.angularForce -= this.leanTorque;
    }

    private updateHandBrake(): void
    {
        if (!this.vehicle || this.vehicle.crashed) return;
        const wheelLeft = this.vehicle.wheelLeft.physics.bodies[0];
        const wheelRight = this.vehicle.wheelRight.physics.bodies[0];

        wheelLeft.angularVelocity *= 0.025;
        wheelRight.angularVelocity *= 0.025;
    }

    private updateWheelDamping(): void
    {
        // Unnatural angular damping for wheels, to be make vehicle slowly loose speed
        // if not accelerating and can serve as 'hand brake' if stopped.
        const wheelLeft = this.vehicle.wheelLeft.physics.bodies[0];
        const wheelRight = this.vehicle.wheelRight.physics.bodies[0];
        const dampingMod = this.acceleration ? -0.05 : 0.01;
        const maxDamping = 0.9;

        this.damping = this.isOn ? Maths.clamp(this.damping + dampingMod, 0, maxDamping) : 0.95;
        wheelLeft.angularDamping = this.damping;
        wheelRight.angularDamping = this.damping;
    }

    /** Clamp lean to 2 levels, making easier for mobile controls  */
    private getNormalisedLean(lean: number): number
    {
        if (lean === 0) return 0;

        return lean < 0 ? -1 : 1;
    }

    private updateIdleRunning(): void
    {
        const wheelLeft = this.vehicle.wheelLeft.physics.bodies[0];
        const wheelRight = this.vehicle.wheelRight.physics.bodies[0];

        wheelLeft.angularVelocity = -12;
        wheelRight.angularVelocity = -12;
        wheelLeft.angularDamping = 0.99;
        wheelRight.angularDamping = 0.99;
    }

    public boost(power = 1): void
    {
        this.boostPower = power;
        if (!power)
        {
            this.boostTime = 0;

            return;
        }

        if (this.boostTime)
        {
            this.boostTime += 1;
        }
        else
        {
            this.boostTime = 3;
        }

        if (this.boostTime > 5) this.boostTime = 5;
    }

    public handBrakeOn(): void
    {
        this.handBrake = true;
    }

    public handBrakeOff(): void
    {
        this.handBrake = false;
    }

    public brake(power = 1): void
    {
        const multiplier = 1 - Maths.clamp(power, 0, 1);
        const wheelLeft = this.vehicle.wheelLeft.physics.bodies[0];
        const wheelRight = this.vehicle.wheelRight.physics.bodies[0];

        wheelLeft.angularVelocity *= multiplier;
        wheelRight.angularVelocity *= multiplier;
        this.torque *= multiplier;
    }
}
